// select on focus from Mozilla Svelte Tutorial 
// https://developer.mozilla.org/en-US/docs/Learn/Tools_and_testing/Client-side_JavaScript_frameworks/Svelte_reactivity_lifecycle_accessibility

export function selectOnFocus(node) {
  if (node && typeof node.select === 'function' ) {               // make sure node is defined and has a select() method
    const onFocus = event => node.select()                        // event handler
    node.addEventListener('focus', onFocus)                       // when node gets focus call onFocus()
    return {
      destroy: () => node.removeEventListener('focus', onFocus)   // this will be executed when the node is removed from the DOM
    }
  }
}

export function focusOnInit(node) {
  if (node && typeof node.focus === 'function') {
    node.focus()
  }
}

// ------------------------------------------------------------------------------
// Reset filter stores, needed when toggling google fonts

import { get } from 'svelte/store'
import { 
  previews_nav_filters, 
  previews_search_filter, 
  previews_weight_filter, 
  previews_tags_filter, 
  previews_tags_filter_operator, 
  previews_are_google_fonts, 
  tags_are_being_edited, 
  previews_storefronts_filter
} from '../stores'

export function resetAllFilters() {
  previews_search_filter.set('')
  previews_weight_filter.set('')
  previews_tags_filter.reset()
  previews_storefronts_filter.reset()
  tags_are_being_edited.set(false)
  if (get(previews_are_google_fonts)) {
    previews_tags_filter_operator.set('or')
    previews_nav_filters.reset()
  } else {
    goToLocallyStoredNavItem()
  }
}

export function resetLocalStorage() {
  localStorage.clear()
}

export function goToLocallyStoredNavItem() {
  let previous_locally_stored_nav_filter = undefined
  try { previous_locally_stored_nav_filter = JSON.parse(localStorage.getItem('previews_nav_filters')) } 
  catch (error) { console.warn('Parse error in locally stored nav filter' + ' - Error: ' + error) }
  if (previous_locally_stored_nav_filter) previews_nav_filters.set(previous_locally_stored_nav_filter)
  else previews_nav_filters.showAll()
}

// CLICK OUTSIDE
// Dispatch event on click outside of node
export function clickOutside(node) {
  const handleClick = event => {
    if (node && !node.contains(event.target) && !event.defaultPrevented) {
      node.dispatchEvent(
        new CustomEvent('click_outside', node)
      )
    }
  }
	document.addEventListener('click', handleClick, true);
  
  return {
    destroy() {
      document.removeEventListener('click', handleClick, true);
    }
	}
}

// ------------------------------------------------------------------------------
// Handle Upsell and Show Pro Badge
import { user, user_valid, modals, buy_modal_initiator } from '../stores'

let modal_timeout

export function handleUpsell(initiator, immediate=false, duration=5000) {
  console.log("handling upsell for: " + initiator)
  if(get(user_valid)) return

  clearTimeout(modal_timeout)
  let wait = immediate ? 0 : duration
  modal_timeout = setTimeout(()=>{
    buy_modal_initiator.set(initiator)
    modals.open('buy')
    console.log('opened buy modal for: ' + initiator)

    if (typeof window.gtag == "undefined") return
    gtag('event', 'buy_modal_shown', {
      'initiator': initiator
    });
  }, wait)
}

export function getFeatureName(initiator) {
  switch (initiator) {
    case 'search':
      return ('Search')
    case 'google':
      return ('Google Fonts')
    case 'theme':
      return ('Night Theme')
    case 'sorting':
      return ('Sorting Options')
    case 'tracking':
      return ('Letter Space Adjustment')
    case 'export':
      return ('Image Exporting')
    case 'print':
      return ('Printing')
    case 'tag_button':
      return ('Tagging')
    case 'save_selection_button':
      return ('Saved Selections')
    case 'load_selection_button':
      return ('Saved Selections')
    case 'highlight':
      return ('Character Highlighting')
    case 'autoscroll':
      return ('Autoscroll')
    case 'ads':
      return ('Remove ads')
    case 'support':
      return ('Support Wordmark')
    case 'fontdrop':
      return ('previewing without installing')
    case 'upload_button':
      return ('Uploading fonts')
    case 'add_to_storefront_button':
      return ('Storefronts')
    default:
      return 'Tagging'
  }
}

export function sendPageViewToAnalytics(title, location) {
  if (typeof window.gtag == "undefined") return
  gtag('event', 'page_view', {
    page_title: title,
    page_location: location,
    send_to: 'G-VMWZX2CGW7'
  });
}

// ------------------------------------------------------------------------------
// List formatting helpers

// General function to format a list of strings with proper comma and "and" placement
export function formatList(items, wrapWithBold = false) {
  if (!items || items.length === 0) return '';
  
  const formatItem = (item) => wrapWithBold ? `<b>${item}</b>` : item;
  
  if (items.length === 1) {
    return formatItem(items[0]);
  }
  if (items.length === 2) {
    return `${formatItem(items[0])} and ${formatItem(items[1])}`;
  }
  
  // For 3 or more items
  const formattedItems = items.map(item => formatItem(item));
  const lastItem = formattedItems.pop();
  return formattedItems.join(', ') + ' and ' + lastItem;
}

// Specific function to format a list of font objects with proper comma and "and" placement
export function formatFontList(fonts, detectedFonts, wrapWithBold = true) {
  if (!fonts || fonts.length === 0) return '';
  
  const fontNames = fonts.map(font => {
    const displayName = detectedFonts.find(f => f.postscript_name === font.postscript_name)?.display_name || font.postscript_name;
    return displayName;
  });
  
  return formatList(fontNames, wrapWithBold);
}